import { createRef } from "react";

import Button from "@/design-system/button";
import ArrowIcon from "@/design-system/icons/arrow-icon";
import type { IComponentAjax, IComponentAjaxState } from "@Components/ComponentAjax";
import { ComponentAjax } from "@Components/ComponentAjax";
import type { IImageLoader } from "@Components/ImageLoader";
import ImageLoader from "@Components/ImageLoader";
import { Loading } from "@Components/Loading";
import * as stylex from "@stylexjs/stylex";

import type { IDiscountsCarousel } from "../discount/DiscountsCarousel";
import { DiscountsCarousel } from "../discount/DiscountsCarousel";

import * as css from "./ShoppingMallTabBrandsItem.module.css";

export interface IShoppingMallTabBrandsItem extends IComponentAjax<IDiscountsCarousel> {
    image: IImageLoader;
    name: string;
    groupKey: string;
    discountsCount: number;
    discounts: null;
}

interface ShoppingMallTabBrandsItemState extends IComponentAjaxState {
    expanded: boolean;
    discounts: IDiscountsCarousel | null;
    renderDiscounts: boolean;
    discountsCount: number;
    userExpanded: boolean;
}

export class ShoppingMallTabBrandsItem extends ComponentAjax<
    IDiscountsCarousel,
    IShoppingMallTabBrandsItem,
    ShoppingMallTabBrandsItemState
> {
    private get name(): JSX.Element {
        if (this.state.discountsCount > 0) {
            return (
                <Button
                    as="button"
                    styleX={styles.button}
                    variant="transparent"
                    size="M"
                    onClick={this.toggle}
                >
                    <h3>{this.props.name}</h3>
                </Button>
            );
        }

        return <h3>{this.props.name}</h3>;
    }

    private get countLink(): JSX.Element {
        if (this.state.discountsCount > 0) {
            return (
                <Button as="button" variant="none" size="M" onClick={this.toggle}>
                    <ArrowIcon direction={this.state.expanded ? "up" : "down"} styleX={styles.arrow} />
                </Button>
            );
        }

        return <span>-</span>;
    }

    private get discounts() {
        if (this.state.discountsCount === 0) {
            return null;
        }

        let slider = null;
        if (this.state.expanded === true && this.state.renderDiscounts === true) {
            slider = (
                <div
                    className={`${css.discounts} ${
                        this.state.discountsCount === 1
                            ? css.brandsCount1
                            : this.state.discountsCount === 2
                              ? css.brandsCount2
                              : ""
                    }`}
                >
                    <DiscountsCarousel {...this.state.discounts} />
                </div>
            );
        } else if (this.state.expanded === true && this.state.discountsCount > 0) {
            slider = <div />;
        }

        return <div ref={this.refElement}>{slider}</div>;
    }

    public static readonly defaultProps: IShoppingMallTabBrandsItem = {
        image: null,
        name: null,
        groupKey: null,
        discountsCount: 0,
        discounts: null,
    };

    private renderTimeout: number | null;

    private refElement = createRef<HTMLDivElement>();

    constructor(props: IShoppingMallTabBrandsItem) {
        super(props);

        this.state = {
            expanded: false,
            discounts: this.props.initialData ? this.props.initialData : null,
            isLoading: false,
            renderDiscounts: false,
            discountsCount: this.props.discountsCount,
            userExpanded: false,
        };

        this.toggle = this.toggle.bind(this);
    }

    public render(): JSX.Element {
        return (
            <div {...this.rootDefaultAttributes(css.shoppingMallTabBrandsItemComponent)}>
                <div className={css.table}>
                    <div className={css.image}>
                        <ImageLoader {...this.props.image} />
                    </div>
                    <div className={css.name}>{this.name}</div>
                    <div className={css.count}>{this.countLink}</div>
                </div>
                {this.discounts}
                {this.state.isLoading && <Loading isModal={true} />}
                {this.renderAjaxErrorContainer()}
            </div>
        );
    }

    protected ajaxSuccess(response: IDiscountsCarousel): void {
        if (!response || !response.discounts || response.discounts.length === 0) {
            this.setState({ isLoading: false, discounts: null });
            return;
        }

        this.setState(
            {
                discounts: response,
                discountsCount: response.discounts.length,
                isLoading: false,
                expanded: true,
                userExpanded: true,
            },
            () => {
                if (this.state.discountsCount === 0) {
                    return;
                }

                this.renderTimeout = window.setTimeout(() => {
                    this.setState({ renderDiscounts: true });
                }, 300);
            },
        );
    }

    protected ajaxFail(): boolean {
        this.setState({ isLoading: false, discounts: null });
        return true;
    }

    private toggle(): void {
        if (this.state.expanded === false && !this.state.discounts) {
            this.fetchDiscounts();
            this.customSlideAnimation(false, 300);
            return;
        }

        if (this.state.expanded === false && this.state.discountsCount > 0) {
            this.renderTimeout = window.setTimeout(() => {
                this.setState({ renderDiscounts: true });
                this.customSlideAnimation(false);
            }, 300);
        }

        if (this.state.expanded === true && this.state.discounts) {
            clearTimeout(this.renderTimeout);
            this.renderTimeout = null;
            this.customSlideAnimation(true);
            this.setState({ renderDiscounts: false });
        }

        this.setState({ expanded: !this.state.expanded, userExpanded: true });
    }

    private fetchDiscounts(): void {
        if (this.state.isLoading === true) {
            return;
        }

        this.setState({ isLoading: true });

        this.ajaxRequest();
    }

    private customSlideAnimation = (hideElement: boolean = false, delayTime: number = 0): object | void => {
        this.refElement.current!.animate(
            {
                height: ["0%", "100%"],
            } as PropertyIndexedKeyframes,
            {
                delay: delayTime,
                endDelay: 0,
                duration: 300,
                fill: "forwards",
                direction: hideElement === false ? "normal" : "reverse",
            },
        );
    };
}

const styles = stylex.create({
    arrow: {
        color: "grey",

        height: 30,
        widows: 30,
    },

    button: {
        padding: "0",
    },
});
